import { RowSelectionState } from "@tanstack/react-table";
import { useEffect, useRef, useState } from "react";
import { useRefinementList } from "react-instantsearch-core";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize
} from "shared/components";
import { CheckDone01, Download01, FilterLines } from "untitledui-js-base";
import { CustomSearchBox } from "views/renters/components/CustomSearchBox";
import { RentersTabs } from "views/renters/components/Header/RentersTabs";
import { SelectedFilters } from "views/renters/components/Header/SelectedFilters";
import { SubscribeModal } from "views/renters/components/Header/SubscribeModal";
import { useProgressContext } from "providers/ProgressContext";
import { PropertyManagementSystemSync } from "shared/components/PropertyManagementSystemSync";
import { useAlgoliaHelper } from "views/renters/utils";
import { useInstantSearch } from "react-instantsearch";

import { CSVLink } from "react-csv";
import { useAuthContext } from "providers/Auth";

type Props = {
  setShowFilters: (value: boolean) => void;
  selectedRentersRow: RowSelectionState;
  clearSelectedRows: () => void;
};

export const RentersHeader = ({
  setShowFilters,
  selectedRentersRow,
  clearSelectedRows
}: Props) => {
  const { results } = useInstantSearch();

  const {
    syncSubscriptions,
    subscriptionsQueue,
    handleRequestPMSsync,
    pmsStatus,
    uploadCsv
  } = useProgressContext();
  const [openSubscribed, setOpenSubscribed] = useState<{
    open: boolean;
    source?: "not_enrolled" | "error";
  }>({
    open: false
  });
  const { items } = useRefinementList({
    attribute: "boomEnrollmentStatus"
  });
  const { type } = useAuthContext();

  const notEnrolled = items.find((el) => el.value === "not_enrolled")?.count;
  const failed = items.find((el) => el.value === "error")?.count || 0;

  const { getAllFilteredRenters } = useAlgoliaHelper();

  const [csvData, setCsvData] = useState<
    {
      "First Name": string;
      "Last Name": string;
      Email: string;
      "Phone Number": string;
      "Date of Birth": string;
      "Property Name": string;
      "Property Address1": string;
      "Property City": string;
      "Property State": string;
      "Property Zip": string;
      Unit: string;
      "Subscription Status": string;
      "Lease Start Date": string;
      "Lease End Date": string;
      "Lease M-2-M": string;
      "Lease Status": string;
      Transactions?: unknown[];
    }[]
  >([]);
  const ref = useRef<HTMLAnchorElement>(null);

  const handleDownloadCSV = async () => {
    getAllFilteredRenters({}).then((renters) => {
      if (!renters) return;
      setCsvData(
        renters.map((renter) => {
          return {
            "First Name": renter.customer.firstName || "--",
            "Last Name": renter.customer.lastName || "--",
            Email: renter.customer.email || "--",
            "Phone Number": renter.customer.phone || "--",
            "Date of Birth": renter.customer.dob || "--",
            "Property Name": renter.property.name,
            "Property Address1": renter.property.address1,
            "Property City": renter.property.city,
            "Property State": renter.property.state,
            "Property Zip": renter.property.zip,
            Unit: renter.unit,
            "Subscription Status":
              renter.boomEnrollmentStatus === "enrolled"
                ? "Active"
                : renter.boomEnrollmentStatus === "not_enrolled"
                  ? "Inactive"
                  : renter.boomEnrollmentStatus === "error"
                    ? "Failed to subscribe"
                    : renter.boomEnrollmentStatus || "--",
            "Lease Start Date": renter.startDate || "--",
            "Lease End Date": renter.endDate || "--",
            "Lease M-2-M":
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              renter.monthToMonth === undefined
                ? "--"
                : String(renter.monthToMonth),
            "Lease Status": renter.status || "--",
            Transactions:
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              renter.transactions.map(
                (transaction) =>
                  `id: (${transaction.id}); type: (${transaction.type || "--"}); amount:(${transaction.amount || "--"}); date: (${transaction.date || "--"}); description: (${transaction.description || "--"});`
              ) || "--"
          };
        })
      );
    });
  };

  useEffect(() => {
    if (csvData.length)
      // @ts-ignore
      ref.current?.link.click() && ref.current.link.blur();
  }, [csvData]);

  return (
    <>
      <h1 className="mb-6 text-2xl font-bold text-content-1000">Renters</h1>
      <div className="flex w-full flex-wrap justify-between gap-2">
        <div className="flex w-fit gap-4">
          <div className="flex gap-2">
            <CustomSearchBox clearSelectedRows={clearSelectedRows} />
            <div className="flex">
              <IconButton
                Icon={FilterLines}
                size={IconButtonSize.SM}
                onClick={() => setShowFilters(true)}
              />
            </div>
          </div>
          <div className="w-fit">
            <PropertyManagementSystemSync
              withDivider={true}
              isSyncing={pmsStatus?.status === "running"}
              handleRequestSync={handleRequestPMSsync}
              lastSuccessSyncedAt={pmsStatus?.last_successful_sync}
            />
          </div>
        </div>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          Icon={Download01}
          onClick={handleDownloadCSV}
        >
          Download CSV
        </Button>
        <CSVLink
          ref={ref}
          data={csvData}
          filename="renters.csv"
          target="_blank"
          className="hidden"
        />
      </div>
      <div className="mt-4 flex flex-wrap justify-between gap-y-2">
        <RentersTabs clearSelectedRows={clearSelectedRows} />
        {Number(notEnrolled) > 0 && (
          <div className="ml-auto flex items-center gap-4">
            <p className="text-sm font-medium text-content-1000 ">
              {notEnrolled} renters not subscribed
            </p>

            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={CheckDone01}
              disabled={subscriptionsQueue.isSyncing || !uploadCsv.canUpload}
              onClick={() =>
                setOpenSubscribed({
                  open: true,
                  source:
                    Object.keys(selectedRentersRow).length > 0
                      ? undefined
                      : "not_enrolled"
                })
              }
            >
              {Object.keys(selectedRentersRow).length > 0
                ? `Subscribe (${Object.keys(selectedRentersRow).length})`
                : "Subscribe All"}
            </Button>
          </div>
        )}
        {type.isBoomOps && !Object.keys(selectedRentersRow).length ? (
          <div className="ml-4 flex items-center gap-4">
            <Button
              variant={ButtonVariant.DANGER}
              size={ButtonSize.SM}
              Icon={CheckDone01}
              disabled={subscriptionsQueue.isSyncing || !uploadCsv.canUpload}
              onClick={() =>
                setOpenSubscribed({
                  open: true,
                  source: "error"
                })
              }
            >
              Subscribe All Failed
            </Button>
          </div>
        ) : null}
      </div>
      <SelectedFilters />

      <SubscribeModal
        isOpen={openSubscribed.open}
        setIsOpen={(v: boolean) =>
          setOpenSubscribed((prev) => ({ ...prev, open: v }))
        }
        rentersCount={
          Object.keys(selectedRentersRow).length > 0
            ? Object.keys(selectedRentersRow).length
            : openSubscribed.source === "error"
              ? failed
              : notEnrolled
        }
        enrollRenters={(enrollmentOption) => {
          if (Object.keys(selectedRentersRow).length > 0) {
            syncSubscriptions({
              renters: results.hits.filter((el) =>
                Object.keys(selectedRentersRow).includes(el.id)
              ),
              enrollmentOption
            });
            // clean selected rows
            clearSelectedRows();
          } else
            getAllFilteredRenters({
              boomEnrollmentStatus: openSubscribed.source
            }).then((renters) => {
              syncSubscriptions({
                renters,
                enrollmentOption
              });
            });
        }}
      />
    </>
  );
};
